import type { Editor, EditorState, MatterOp } from "../../app/editor";
import { DEFAULT_PALETTES } from "../../core/color";
import { SHAPE_LABELS, type ShapeKind } from "../../physics/shapes";
import { DEFAULT_BRUSH, DYNAMICS_INFO, ERASE_MODE_LABELS, SMOOTH_MODE_INFO, SMOOTH_MODE_LABELS, type BrushMode, type EraseMode, type SmoothMode, type StrokeDynamics } from "../../stroke/types";
import { SYMMETRY_LABELS, type SymmetryMode } from "../../symmetry/symmetry";
import { PULL_LABELS, type PullFamily } from "../../tools/pull-shapes";
import { TOOL_BY_ID, type ToolId } from "../../tools/manifest";
import type { Tool3D } from "../../scene3d/tools3d";
import { DRAW_PLANE_LABELS, DRAW_PLANE_ORDER, type DrawPlaneKind } from "../../scene3d/drawplane";
import { GIZMO_MODE_LABELS, GIZMO_MODE_ORDER, type GizmoMode } from "../../scene3d/gizmo";
import { PRIMITIVE_LABELS, PRIMITIVE_ORDER, type PrimitiveKind } from "../../scene3d/objects";
import {
  PLACEMENT_LABELS,
  PLACEMENT_ORDER,
  STROKE_TARGET_LABELS,
  STROKE_TARGET_ORDER,
  type StrokePlacement,
  type StrokeTarget,
} from "../../scene3d/placement";
import {
  SNAP_AXIS_LABELS,
  SNAP_AXIS_ORDER,
  SNAP_LABELS,
  SNAP_PICK_LABELS,
  SNAP_SOURCE_ORDER,
  type SnapAxis,
  type SnapPick,
  type SnapSource,
} from "../../scene3d/snap";
import type { BridgeStyle } from "../../render/field-gl";
import type { HotNode, MenuHooks } from "../hotbox/menu";

/**
 * Fuente única declarativa de la interfaz.
 *
 * Cada opción editable (rango, etiqueta, icono, ayuda, cómo se lee y se escribe
 * en el editor, cuándo se muestra) se declara UNA sola vez aquí. El dock lateral
 * y el menú radial son dos renderizadores de este mismo esquema: añadir o editar
 * una opción se hace en un único sitio y aparece en las dos superficies.
 *
 * Lo genuinamente propio de cada superficie —el selector HSV y la vista previa
 * del dock, la rueda y la activación de herramientas del radial— se expresa como
 * campos `custom` con "escotillas" (`renderDock`/`renderRadial`) o acotando el
 * campo a una superficie con `surfaces`.
 */

/** Valor fijo o derivado del estado (etiquetas, iconos, ayudas y acentos vivos). */
export type Dyn<T> = T | ((s: EditorState) => T);

/** Resuelve un valor `Dyn<T>` contra el estado actual. */
export function val<T>(v: Dyn<T>, s: EditorState): T {
  return typeof v === "function" ? (v as (s: EditorState) => T)(s) : v;
}

export type SurfaceId = "dock" | "radial";

export interface FieldBase {
  id: string;
  label: Dyn<string>;
  icon?: Dyn<string>;
  hint?: Dyn<string>;
  /** Color de acento del sector/control (muestras, categorías destacadas). */
  accent?: Dyn<string>;
  /** Cuándo el campo aplica. Por defecto: siempre. */
  visible?: (s: EditorState) => boolean;
  /** Radial SIEMPRE poda; el dock atenúa ("dim") u oculta ("prune"). */
  whenHidden?: "prune" | "dim";
  disabled?: (s: EditorState) => boolean;
  /** Superficies en las que aparece. Por defecto ambas. */
  surfaces?: SurfaceId[];
}
export interface NumberField extends FieldBase {
  kind: "number";
  min: number;
  max: number;
  step?: number;
  gamma?: number;
  unit?: string;
  decimals?: number;
  get: (s: EditorState) => number;
  set: (v: number) => void;
}

export interface ToggleField extends FieldBase {
  kind: "toggle";
  get: (s: EditorState) => boolean;
  set: (v: boolean) => void;
}

export interface ChoiceOption {
  value: string;
  /** Id del nodo/control generado (se preserva: `dyn-*`, `shape-*`, `sym-*`…). */
  id: string;
  label: string;
  icon?: string;
  accent?: string;
  title?: string;
}

export interface ChoiceField extends FieldBase {
  kind: "choice";
  /** Pista SOLO para el dock; el radial siempre usa un submenú de acciones. */
  chooser?: "segmented" | "select";
  options: ChoiceOption[];
  get: (s: EditorState) => string;
  set: (v: string) => void;
}

export interface ActionField extends FieldBase {
  kind: "action";
  run: () => void;
  /** No cerrar el radial tras ejecutar (toggles, ajustes rápidos). */
  keepOpen?: boolean;
  /** Estado "activo/encendido" (toggles y herramientas seleccionadas). */
  toggled?: (s: EditorState) => boolean;
  danger?: boolean;
}

export interface SwatchesField extends FieldBase {
  kind: "swatches";
  colors: (s: EditorState) => readonly string[];
  value: (s: EditorState) => string;
  onPick: (hex: string) => void;
}

export interface CustomField extends FieldBase {
  kind: "custom";
  renderDock?: (editor: Editor) => { el: HTMLElement; sync?: (s: EditorState) => void };
  renderRadial?: (s: EditorState) => HotNode | HotNode[] | null;
}

export type Field = NumberField | ToggleField | ChoiceField | ActionField | SwatchesField | CustomField;

export type Layout = "stack" | "row" | "inline" | "brush-by-mode";

/**
 * Configuración del pincel por modos: el radial la despliega como tres submenús
 * (Trazo/Relleno/Arrastre) que activan el modo y muestran sus opciones; el dock
 * la rinde como un control segmentado + las opciones planas.
 */
export interface BrushByMode {
  modes: { value: BrushMode; label: string; icon: string; title?: string }[];
  current: (s: EditorState) => BrushMode;
  isToolActive: (s: EditorState) => boolean;
  /** Radial: activa herramienta pincel + fija el modo. */
  activate: (m: BrushMode) => void;
  /** Dock: solo fija el modo. */
  select: (m: BrushMode) => void;
}

export interface Group {
  id: string;
  label?: Dyn<string>;
  icon?: Dyn<string>;
  hint?: Dyn<string>;
  accent?: Dyn<string>;
  visible?: (s: EditorState) => boolean;
  whenHidden?: "prune" | "dim";
  surfaces?: SurfaceId[];
  layout?: Layout;
  brushByMode?: BrushByMode;
  children: SchemaNode[];
}

export type SchemaNode = Field | Group;

/** Grupo de nivel superior: un submenú radial y una página/pestaña del dock. */
export interface Domain extends Group {
  icon: Dyn<string>;
}

/** Discrimina grupo de campo sin depender de `kind` (los grupos no lo llevan). */
export function isGroup(n: SchemaNode): n is Group {
  return (n as Group).children !== undefined;
}
const MODE_LABELS: Record<BrushMode, string> = {
  stroke: "Trazo",
  fill: "Relleno",
  pull: "Arrastre",
  erase: "Borrador",
};

const MODE_ICONS: Record<BrushMode, string> = {
  stroke: "brush",
  fill: "droplet",
  pull: "matter",
  erase: "eraser",
};

/** Numero de atajo de teclado por modo (Trazo 1 / Relleno 2 / Arrastre 3 / Borrador 4). */
const MODE_KEYS: Record<BrushMode, number> = { stroke: 1, fill: 2, pull: 3, erase: 4 };

const usesPressure = (d: StrokeDynamics): boolean => d === "pressure" || d === "pressure-velocity";
const usesVelocity = (d: StrokeDynamics): boolean => d === "velocity" || d === "pressure-velocity";

/**
 * Accion de herramienta derivada del manifiesto.
 *
 * Se ESPARCE en su sitio dentro del dominio en vez de inyectarse al final del
 * arbol: la posicion de cada herramienta en el radial es una decision de diseno,
 * y `buildSchema` es el documento de orden. Un inyector guiado por un ancla
 * fallaria en silencio si alguien renombrara ese campo — y el radial, tras
 * retirar el rail, es el UNICO conmutador de herramientas de la aplicacion.
 *
 * El `id` del nodo lo manda el manifiesto y es CONTRATO: los chips del radial lo
 * persisten en localStorage y `resolveNode` resuelve por `{path, nodeId}`.
 */
function toolAction(id: ToolId, editor: Editor): ActionField {
  const spec = TOOL_BY_ID[id];
  const radial = spec.radial;
  if (!radial) throw new Error(`La herramienta "${id}" no declara accion radial`);

  const field: ActionField = {
    kind: "action",
    id: radial.nodeId,
    label: radial.label,
    icon: radial.icon ?? spec.icon,
    run: () => editor.setTool(id),
    toggled: (s) => s.tool === id,
  };
  if (radial.surfaces) field.surfaces = [...radial.surfaces];
  return field;
}

/**
 * Construye el esquema completo a partir del editor. Las lecturas (`get`,
 * `visible`, `toggled`…) se hacen sobre el `state` que cada superficie pasa al
 * renderizar; las escrituras van al editor. Las relecturas cruzadas (p. ej. la
 * otra componente de la gravedad) usan `editor.state` para tomar el valor vivo.
 */
export function buildSchema(editor: Editor, _state: EditorState, hooks: MenuHooks): Domain[] {
  // ------------------------------------------------------------- Pincel
  // Opciones comunes a los tres modos. El radial las clona bajo cada modo; el
  // dock las muestra una vez. "Familia" solo aplica en Arrastre (pull).
  const brushOptions: SchemaNode[] = [
    { kind: "number", id: "size", label: "Tamano", icon: "plus", min: 0.5, max: 400, step: 0.5, gamma: 2.2, unit: "px", decimals: 1, hint: "Diametro base ([ y ] lo cambian sin soltar el lapiz).", get: (s) => s.brush.size, set: (v) => editor.setBrush({ size: v }) },
    { kind: "number", id: "opacity", label: "Opacidad", icon: "droplet", min: 0.02, max: 1, step: 0.01, decimals: 2, get: (s) => s.brush.opacity, set: (v) => editor.setBrush({ opacity: v }) },
    // Vista previa del trazo: cromo propio del dock (el radial no la muestra).
    // El adaptador del dock la renderiza por id; aquí solo se declara su lugar.
    { kind: "custom", id: "brush-preview", label: "Vista previa", surfaces: ["dock"], visible: (s) => s.brush.mode !== "erase" },
    {
      kind: "choice",
      id: "dynamics",
      label: "Dinamica",
      icon: "tune",
      chooser: "select",
      visible: (s) => s.brush.mode !== "erase",
      hint: (s) => DYNAMICS_INFO[s.brush.dynamics].hint,
      options: (Object.keys(DYNAMICS_INFO) as StrokeDynamics[]).map((k) => ({ value: k, id: `dyn-${k}`, label: DYNAMICS_INFO[k].label })),
      get: (s) => s.brush.dynamics,
      set: (v) => editor.setBrush({ dynamics: v as StrokeDynamics }),
    },
    // Nota viva bajo el selector de dinámica (solo dock): su texto sigue al estado.
    { kind: "custom", id: "brush-dyn-hint", label: "Dinamica", surfaces: ["dock"], visible: (s) => s.brush.mode !== "erase" },
    {
      id: "response",
      label: "Respuesta",
      icon: "tune",
      hint: "Cómo el lápiz responde a presión y velocidad",
      visible: (s) => s.brush.mode !== "erase",
      children: [
        { kind: "choice", id: "smooth-mode", label: "Modo", chooser: "select", hint: "Como Lazy Nezumi Pro: elige cómo se estabiliza la línea.", options: (Object.keys(SMOOTH_MODE_LABELS) as SmoothMode[]).map((k) => ({ value: k, id: `smooth-${k}`, label: SMOOTH_MODE_LABELS[k], title: SMOOTH_MODE_INFO[k] })), get: (s) => (s.brush.smoothMode ?? "pulled-string") as string, set: (v) => editor.setBrush({ smoothMode: v as SmoothMode }) },
        { kind: "number", id: "smooth-amount", label: "Cantidad", min: 0, max: 1, step: 0.01, decimals: 2, hint: "Como LZ: más cantidad = más estabilización. Pulled String usa su propia cuerda (slider de abajo). Media/EMA: ventana/alfa. Inercia: masa.", get: (s) => (s.brush.smoothAmount ?? (s.brush as unknown as { smoothing: number }).smoothing ?? 0.35), set: (v) => editor.setBrush({ smoothAmount: v, smoothing: v }) },
        { kind: "number", id: "string-length", label: "Cuerda", min: 0, max: 1, step: 0.01, decimals: 2, unit: "px/ms", hint: "Longitud de la cuerda del estabilizador. Cuanto más larga, antes se estabiliza y más se ve la cuerda colgando. Vale para cualquier modo (como LZ: su cuerda guía es independiente del suavizado). 0 = sin cuerda visible.", get: (s) => (s.brush as unknown as { stringLength?: number }).stringLength ?? (s.brush.smoothAmount ?? 0.35), set: (v) => editor.setBrush({ stringLength: v } as unknown as Partial<import("../../stroke/types").BrushSettings>) },
        { kind: "number", id: "string-curve", label: "Gravedad pliegue", min: 0, max: 1.5, step: 0.05, decimals: 2, hint: "Como tu simulación: gravityFactor. 0 = recta, 0.65 = por defecto (tu valor), 1.5 = U muy profunda. holgura * gravedad.", get: (s) => (s.brush as unknown as { stringCurve?: number }).stringCurve ?? 0.65, set: (v) => editor.setBrush({ stringCurve: v } as unknown as Partial<import("../../stroke/types").BrushSettings>) },
        { kind: "number", id: "string-drop", label: "Caída extra", min: 0, max: 1, step: 0.01, decimals: 2, hint: "Suma a la gravedad del pliegue cuando el simulador la pasa como drop (compat).", get: (s) => (s.brush as unknown as { stringDrop?: number }).stringDrop ?? 0.14, set: (v) => editor.setBrush({ stringDrop: v } as unknown as Partial<import("../../stroke/types").BrushSettings>) },
        { kind: "number", id: "string-swing", label: "Balanceo", min: 0, max: 1, step: 0.05, decimals: 2, hint: "0 = U estática inmediata como tu HTML. >0 = cuerda viva con inercia/rebote.", get: (s) => (s.brush as unknown as { stringSwing?: number }).stringSwing ?? 0, set: (v) => editor.setBrush({ stringSwing: v } as unknown as Partial<import("../../stroke/types").BrushSettings>) },
        { kind: "choice", id: "string-style", label: "Estilo", chooser: "segmented", hint: "Presets rápidos: Tensa casi recta, Clásica = tu HTML (0.65), Colgante = U profunda.", options: [{ value: "tensa", id: "style-tensa", label: "Tensa" }, { value: "clasica", id: "style-clasica", label: "Clásica" }, { value: "colgante", id: "style-colgante", label: "Colgante" }], get: (s) => (s.brush as unknown as { stringStyle?: string }).stringStyle ?? "clasica", set: (v) => { const m = v as "tensa" | "clasica" | "colgante"; const p = m === "tensa" ? { stringStyle: m, stringCurve: 0.2, stringDrop: 0.06, stringSwing: 0 } : m === "colgante" ? { stringStyle: m, stringCurve: 1.15, stringDrop: 0.3, stringSwing: 0.55 } : { stringStyle: m, stringCurve: 0.65, stringDrop: 0.14, stringSwing: 0 }; editor.setBrush(p as unknown as Partial<import("../../stroke/types").BrushSettings>); } },
        { kind: "toggle", id: "smooth-catchup", label: "Catch Up (solo lápiz)", hint: "En Media / EMA, al levantar el lápiz la línea alcanza al cursor. Solo aplica con esos modos.", visible: (s) => { const m = (s.brush.smoothMode ?? "pulled-string") as SmoothMode; return m === "moving-average" || m === "ema"; }, get: (s) => !!s.brush.smoothCatchUp, set: (v) => editor.setBrush({ smoothCatchUp: v }) },
        { kind: "number", id: "smoothing", label: "Jitter fino", min: 0, max: 1, step: 0.01, decimals: 2, hint: "Filtro fino del sensor (jitter base). Ya no es el suavizado artístico.", get: (s) => s.brush.smoothing, set: (v) => editor.setBrush({ smoothing: v }) },
        { kind: "number", id: "streamline", label: "Estabilizador (legacy)", min: 0, max: 0.95, step: 0.01, decimals: 2, hint: "Migrado a Modos arriba. Déjalo en 0 y usa Modo/Cantidad.", get: (s) => s.brush.streamline, set: (v) => editor.setBrush({ streamline: v }) },
        { kind: "number", id: "min-ratio", label: "Ancho minimo", min: 0, max: 1, step: 0.01, decimals: 2, hint: "Fraccion del diametro a la que puede llegar la dinamica.", get: (s) => s.brush.minRatio, set: (v) => editor.setBrush({ minRatio: v }) },
        { kind: "number", id: "pressure-curve", label: "Curva de presion", min: -1, max: 1, step: 0.05, decimals: 2, hint: "Negativo: responde antes con poca fuerza. Positivo: exige apretar mas.", visible: (s) => usesPressure(s.brush.dynamics), whenHidden: "dim", get: (s) => s.brush.pressureCurve, set: (v) => editor.setBrush({ pressureCurve: v }) },
        { kind: "number", id: "velocity-scale", label: "Escala de velocidad", min: 0.2, max: 6, step: 0.05, decimals: 2, unit: "px/ms", hint: "Velocidad de referencia: por encima de ella el trazo llega a su extremo.", visible: (s) => usesVelocity(s.brush.dynamics), whenHidden: "dim", get: (s) => s.brush.velocityScale, set: (v) => editor.setBrush({ velocityScale: v }) },
        { kind: "toggle", id: "velocity-invert", label: "Rapido = grueso", icon: "spark", hint: "Apagado imita la tinta (rapido afina). Encendido imita el pincel seco.", visible: (s) => usesVelocity(s.brush.dynamics), whenHidden: "dim", get: (s) => s.brush.velocityInvert, set: (v) => editor.setBrush({ velocityInvert: v }) },
        { kind: "custom", id: "note-stabilize", label: "Nota", surfaces: ["dock"], hint: "El suavizado corrige el temblor y el estabilizador la direccion. Si notas la punta lenta, baja el estabilizador antes que el suavizado." },
      ],
    },
    {
      id: "profile",
      label: "Perfil",
      icon: "brush",
      hint: "Afilado de los extremos y textura del trazo",
      visible: (s) => s.brush.mode !== "erase",
      children: [
        { kind: "number", id: "taper-in", label: "Afilado inicial", min: 0, max: 0.5, step: 0.01, decimals: 2, get: (s) => s.brush.taperIn, set: (v) => editor.setBrush({ taperIn: v }) },
        { kind: "number", id: "taper-out", label: "Afilado final", min: 0, max: 0.5, step: 0.01, decimals: 2, get: (s) => s.brush.taperOut, set: (v) => editor.setBrush({ taperOut: v }) },
        { kind: "number", id: "jitter", label: "Temblor", min: 0, max: 1, step: 0.01, decimals: 2, hint: "Ruido de ancho: da textura de carboncillo.", get: (s) => s.brush.jitter, set: (v) => editor.setBrush({ jitter: v }) },
      ],
    },
    { kind: "toggle", id: "gradient", label: "Degradado", icon: "layers", hint: "Desvanece el trazo hacia abajo (modificador de Alchemy). Tecla G.", visible: (s) => s.brush.mode !== "erase", get: (s) => s.brush.gradient, set: (v) => editor.setBrush({ gradient: v }) },
    { kind: "toggle", id: "splat", label: "Splat", icon: "droplet", hint: "Contorno anguloso en vez de suave (modificador de Alchemy). Tecla P.", visible: (s) => s.brush.mode !== "erase", get: (s) => s.brush.splat, set: (v) => editor.setBrush({ splat: v }) },
    { kind: "toggle", id: "as-matter", label: "Hacer materia", icon: "matter", hint: "Convierte el trazo, relleno o arrastre en materia sin cambiar de modo del pincel. Tecla 5.", visible: (s) => s.brush.mode !== "erase", get: (s) => s.brush.asMatter, set: (v) => editor.setBrush({ asMatter: v }) },
    // ---- Acuarela: el mismo pincel pinta con fluidos (pintura humeda). ----
    // Es un toggle hermano de "Hacer materia": al activarlo, el gesto inyecta
    // pigmento/agua en el plano de acuarela y aparecen sus ajustes (mapean 1:1
    // a AquaParams, la fuente de datos unica del motor).
    { kind: "toggle", id: "as-aqua", label: "Acuarela", icon: "droplet", hint: "El pincel pinta con fluidos: la tinta fluye, se difumina y se seca sobre el papel. Como 'Hacer materia', pero humedo.", visible: (s) => s.brush.mode !== "erase" && s.aquaAvailable && !s.brush.asMatter && !s.brush.asRaster, get: (s) => s.brush.asAqua, set: (v) => editor.setBrush({ asAqua: v }) },
    {
      kind: "choice",
      id: "aqua-mode",
      label: "Moja o pigmenta",
      icon: "droplet",
      chooser: "segmented",
      hint: "Pluma: deposita pigmento en linea. Agua: moja el papel y empuja el fluido, mezclando el pigmento que ya hay.",
      visible: (s) => s.brush.asAqua && s.brush.mode !== "erase",
      options: [
        { value: "pen", id: "aqua-pen", label: "Pluma" },
        { value: "brush", id: "aqua-brush", label: "Agua" },
      ],
      get: (s) => s.aquaMode,
      set: (v) => editor.setAquaMode(v as "pen" | "brush"),
    },
    // Tamaño propio de la acuarela, INDEPENDIENTE por modo: pluma fina / agua
    // ancha se recuerdan por separado (cambiar de modo trae su tamaño).
    { kind: "number", id: "aqua-size", label: (s) => (s.aquaMode === "brush" ? "Tamaño agua" : "Tamaño pluma"), min: 1, max: 400, step: 1, gamma: 1.5, unit: "px", visible: (s) => s.brush.asAqua && s.brush.mode !== "erase", get: (s) => s.aquaSize, set: (v) => editor.setAquaSize(v) },
    { kind: "number", id: "aqua-flow", label: "Flujo", min: 0, max: 1, step: 0.01, decimals: 2, hint: "Energia del fluido: a mas flujo, mas movimiento y remolinos.", visible: (s) => s.brush.asAqua && s.brush.mode !== "erase", get: (s) => s.aquaParams.flow, set: (v) => editor.setAquaParam("flow", v) },
    { kind: "number", id: "aqua-bleed", label: "Sangrado", min: 0, max: 1, step: 0.01, decimals: 2, hint: "Cuanto se difunde el pigmento hacia los bordes (los halos de acuarela).", visible: (s) => s.brush.asAqua && s.brush.mode !== "erase", get: (s) => s.aquaParams.bleed, set: (v) => editor.setAquaParam("bleed", v) },
    { kind: "number", id: "aqua-dry", label: "Secado", min: 0, max: 1, step: 0.01, decimals: 2, hint: "Velocidad a la que el papel se seca: alto = la tinta deja de fluir antes.", visible: (s) => s.brush.asAqua && s.brush.mode !== "erase", get: (s) => s.aquaParams.dry, set: (v) => editor.setAquaParam("dry", v) },
    { kind: "number", id: "aqua-color", label: "Matiz", min: 0, max: 1, step: 0.01, decimals: 2, hint: "Tiñe el sangrado de calido a frio (azules y violetas tipicos).", visible: (s) => s.brush.asAqua && s.brush.mode !== "erase", get: (s) => s.aquaParams.color, set: (v) => editor.setAquaParam("color", v) },
    { kind: "number", id: "aqua-ink", label: "Carga de agua", min: 0, max: 1, step: 0.01, decimals: 2, hint: "Pigmento que lleva el pincel de agua (0 = solo agua, limpia).", visible: (s) => s.brush.asAqua && s.aquaMode === "brush" && s.brush.mode !== "erase", get: (s) => s.aquaParams.brushInk, set: (v) => editor.setAquaParam("brushInk", v) },
    { kind: "action", id: "aqua-fix", label: "Hornear acuarela", icon: "bake", hint: "Seca la capa: el pigmento deja de fluir y pasa a ser un rastro fijo del dibujo, que ya acompaña al paneo y al zoom y entra en el guardado y en el PNG.", visible: (s) => s.brush.asAqua && s.brush.mode !== "erase", run: () => editor.aquaFix() },
    { kind: "action", id: "aqua-clear", label: "Limpiar acuarela", icon: "trash", danger: true, hint: "Vacia la capa de acuarela de destino: el fluido y lo que ya estuviera seco.", visible: (s) => s.brush.asAqua && s.brush.mode !== "erase", run: () => editor.aquaClear() },
    { kind: "action", id: "aqua-new-layer", label: "Nueva capa de acuarela", icon: "droplet", hint: "Añade otra capa de acuarela con su propio fluido, para no mezclarla con la que ya hay. Tambien esta en el pie del panel de capas.", visible: (s) => s.brush.asAqua && s.brush.mode !== "erase", run: () => editor.addAquaLayer() },
    { kind: "toggle", id: "aqua-visible", label: "Capa visible", icon: "layers", hint: "Muestra u oculta la capa de acuarela de destino (lo mismo que su ojo en el panel de capas).", visible: (s) => s.brush.asAqua && s.brush.mode !== "erase", get: (s) => s.aquaLayerVisible, set: (v) => editor.setAquaVisible(v) },
    { kind: "number", id: "aqua-opacity", label: "Opacidad capa", min: 0, max: 1, step: 0.01, decimals: 2, hint: "Opacidad de la capa de acuarela de destino. Su fusion, mascara y orden se ajustan en el panel de capas, como en cualquier otra capa.", visible: (s) => s.brush.asAqua && s.brush.mode !== "erase", get: (s) => s.aquaLayerOpacity, set: (v) => editor.setAquaOpacity(v) },
    // ---- Pincel raster (tipo Photoshop): estampa una punta sobre pixeles. ----
    // Toggle hermano de "Hacer materia"/"Acuarela": al activarlo el gesto estampa
    // sobre una capa de pixeles en vez de producir tinta vectorial. El tamano y
    // la opacidad los toma de los controles de arriba (comunes a todos los modos);
    // aqui solo lo propio del raster.
    { kind: "toggle", id: "as-raster", label: "Pincel raster", icon: "brush", hint: "Pincel tipo Photoshop: estampa una punta con textura a lo largo del trazo sobre una capa de pixeles. Rapido y sin vectores. Como 'Acuarela', pero seco.", visible: (s) => s.brush.mode !== "erase" && !s.brush.asMatter && !s.brush.asAqua, get: (s) => s.brush.asRaster, set: (v) => editor.setBrush({ asRaster: v }) },
    { kind: "number", id: "raster-hardness", label: "Dureza", icon: "brush", min: 0, max: 1, step: 0.01, decimals: 2, hint: "Dureza del borde de la punta: 1 es un disco neto, 0 un degradado suave desde el centro.", visible: (s) => s.brush.asRaster && s.brush.mode !== "erase", get: (s) => s.brush.rasterHardness, set: (v) => editor.setBrush({ rasterHardness: v }) },
    { kind: "number", id: "raster-spacing", label: "Espaciado", icon: "tune", min: 0.01, max: 1, step: 0.01, decimals: 2, hint: "Separacion entre estampas, fraccion del diametro. Bajo = trazo continuo; alto = sello discontinuo.", visible: (s) => s.brush.asRaster && s.brush.mode !== "erase", get: (s) => s.brush.rasterSpacing, set: (v) => editor.setBrush({ rasterSpacing: v }) },
    { kind: "number", id: "raster-flow", label: "Flujo", icon: "droplet", min: 0.02, max: 1, step: 0.01, decimals: 2, hint: "Pintura que suelta cada estampa. La opacidad de arriba limita el total del trazo por mucho que se solapen.", visible: (s) => s.brush.asRaster && s.brush.mode !== "erase", get: (s) => s.brush.rasterFlow, set: (v) => editor.setBrush({ rasterFlow: v }) },
    { kind: "number", id: "raster-angle", label: "Angulo", icon: "tune", min: 0, max: 360, step: 1, unit: "grados", visible: (s) => s.brush.asRaster && s.brush.mode !== "erase", get: (s) => s.brush.rasterAngle, set: (v) => editor.setBrush({ rasterAngle: v }) },
    { kind: "number", id: "raster-roundness", label: "Redondez", icon: "tune", min: 0.05, max: 1, step: 0.01, decimals: 2, hint: "1 = punta redonda; por debajo se achata y da un trazo caligrafico.", visible: (s) => s.brush.asRaster && s.brush.mode !== "erase", get: (s) => s.brush.rasterRoundness, set: (v) => editor.setBrush({ rasterRoundness: v }) },
    { kind: "toggle", id: "raster-angle-follow", label: "Angulo sigue al trazo", icon: "tune", hint: "La punta gira para seguir la direccion del trazo (puntas planas que caligrafian solas).", visible: (s) => s.brush.asRaster && s.brush.mode !== "erase", get: (s) => s.brush.rasterAngleFollow, set: (v) => editor.setBrush({ rasterAngleFollow: v }) },
    {
      id: "raster-dyn",
      label: "Dinamica del pincel",
      icon: "spark",
      hint: "Dispersion y variacion al azar, como las pestanas de Photoshop.",
      visible: (s) => s.brush.asRaster && s.brush.mode !== "erase",
      children: [
        { kind: "number", id: "raster-scatter", label: "Dispersion", min: 0, max: 3, step: 0.05, decimals: 2, unit: "diam", hint: "Esparce las huellas al azar alrededor del trazo, medido en diametros.", get: (s) => s.brush.rasterScatter, set: (v) => editor.setBrush({ rasterScatter: v }) },
        { kind: "number", id: "raster-scatter-count", label: "Huellas por paso", min: 1, max: 8, step: 1, decimals: 0, hint: "Cuantas huellas se sueltan en cada punto del trazo (con dispersion, un aerosol).", get: (s) => s.brush.rasterScatterCount, set: (v) => editor.setBrush({ rasterScatterCount: Math.round(v) }) },
        { kind: "number", id: "raster-size-jitter", label: "Jitter de tamano", min: 0, max: 1, step: 0.01, decimals: 2, get: (s) => s.brush.rasterSizeJitter, set: (v) => editor.setBrush({ rasterSizeJitter: v }) },
        { kind: "number", id: "raster-angle-jitter", label: "Jitter de angulo", min: 0, max: 1, step: 0.01, decimals: 2, get: (s) => s.brush.rasterAngleJitter, set: (v) => editor.setBrush({ rasterAngleJitter: v }) },
        { kind: "number", id: "raster-roundness-jitter", label: "Jitter de redondez", min: 0, max: 1, step: 0.01, decimals: 2, get: (s) => s.brush.rasterRoundnessJitter, set: (v) => editor.setBrush({ rasterRoundnessJitter: v }) },
        { kind: "number", id: "raster-flow-jitter", label: "Jitter de flujo", min: 0, max: 1, step: 0.01, decimals: 2, get: (s) => s.brush.rasterFlowJitter, set: (v) => editor.setBrush({ rasterFlowJitter: v }) },
        { kind: "number", id: "raster-opacity-jitter", label: "Jitter de opacidad", min: 0, max: 1, step: 0.01, decimals: 2, hint: "Variacion de opacidad por estampa (Transfer de Photoshop).", get: (s) => s.brush.rasterOpacityJitter, set: (v) => editor.setBrush({ rasterOpacityJitter: v }) },
      ],
    },
    {
      id: "raster-transfer",
      label: "Transferencia (presión)",
      icon: "droplet",
      hint: "Como Photoshop: la presión modula opacidad y flujo por estampa. A 0 no afecta, a 1 a baja presión la estampa es casi transparente.",
      visible: (s) => s.brush.asRaster && s.brush.mode !== "erase",
      children: [
        { kind: "number", id: "raster-pressure-opacity", label: "Presión → opacidad", min: 0, max: 1, step: 0.05, decimals: 2, get: (s) => s.brush.rasterPressureOpacity, set: (v) => editor.setBrush({ rasterPressureOpacity: v }) },
        { kind: "number", id: "raster-pressure-flow", label: "Presión → flujo", min: 0, max: 1, step: 0.05, decimals: 2, get: (s) => s.brush.rasterPressureFlow, set: (v) => editor.setBrush({ rasterPressureFlow: v }) },
      ],
    },
    { kind: "custom", id: "brush-presets", label: "Pinceles", surfaces: ["dock"], visible: (s) => s.brush.asRaster && s.brush.mode !== "erase" },
    // ---- Espacio 3D: el mismo pincel dibuja en el espacio. ----
    // Mismo patron que "Hacer materia" y "Acuarela": un interruptor y, debajo,
    // solo lo que aplica cuando esta encendido. Los modos del pincel NO se
    // repiten aqui: los manda el selector de arriba, que es el mismo, y en el
    // espacio significan lo mismo que en el lienzo.
    { kind: "toggle", id: "space-on", label: "Espacio 3D", icon: "cube", hint: "Dibuja en el espacio. Orbita con el boton central o el derecho, mueve el plano con [ y ], y encuadra con F.", disabled: (s) => !s.scene3dAvailable, get: (s) => s.mode3d, set: (v) => editor.setMode3D(v) },
    {
      id: "space-options",
      label: "Espacio",
      icon: "cube",
      visible: (s) => s.mode3d,
      children: [
        {
          kind: "choice",
          id: "space-tool",
          label: "Herramienta",
          icon: "cube",
          chooser: "segmented",
          hint: "Pincel dibuja con los modos de arriba. Suavizar relaja los trazos. Objetos inserta y mueve primitivas con el gizmo.",
          options: [
            { value: "brush", id: "space-tool-brush", label: "Pincel" },
            { value: "smooth", id: "space-tool-smooth", label: "Suavizar" },
            { value: "object", id: "space-tool-object", label: "Objetos" },
          ],
          get: (s) => s.scene3dSettings.tool,
          set: (v) => editor.setScene3D({ tool: v as Tool3D }),
        },
        {
          id: "space-objects",
          label: "Objetos",
          icon: "cube",
          visible: (s) => s.scene3dSettings.tool === "object",
          children: [
            {
              kind: "choice",
              id: "primitive-kind",
              label: "Primitiva",
              icon: "cube",
              chooser: "select",
              options: PRIMITIVE_ORDER.map((k) => ({
                value: k,
                id: `primitive-${k}`,
                label: PRIMITIVE_LABELS[k],
              })),
              get: (s) => s.scene3dSettings.primitive,
              set: (v) => editor.setScene3D({ primitive: v as PrimitiveKind }),
            },
            {
              kind: "action",
              id: "primitive-insert",
              label: "Insertar",
              icon: "plus",
              hint: "Mete la primitiva elegida delante de la cámara. Luego se agarra con el gizmo.",
              run: () => editor.insertPrimitive(editor.state.scene3dSettings.primitive),
            },
            {
              kind: "choice",
              id: "gizmo-mode",
              label: "Transformar",
              icon: "move",
              chooser: "segmented",
              hint: "El gizmo del objeto seleccionado. Teclas W (mover), E (rotar), R (escalar), como en los programas de 3D. Ejes de color: X rojo, Y verde, Z azul.",
              visible: (s) => s.selectedObject !== null,
              options: GIZMO_MODE_ORDER.map((k) => ({
                value: k,
                id: `gizmo-${k}`,
                label: GIZMO_MODE_LABELS[k],
              })),
              get: (s) => s.scene3dSettings.gizmo,
              set: (v) => editor.setScene3D({ gizmo: v as GizmoMode }),
            },
            {
              kind: "action",
              id: "object-delete",
              label: "Borrar objeto",
              icon: "trash",
              danger: true,
              disabled: (s) => s.selectedObject === null,
              run: () => editor.deleteSelectedObject(),
            },
            {
              id: "object-material",
              label: "Material",
              icon: "palette",
              hint: "El material físico (PBR) del objeto seleccionado: cómo responde a la luz. Rugosidad, metalicidad y emisión, como en cualquier editor 3D.",
              visible: (s) => s.selectedObject !== null,
              children: [
                {
                  kind: "number",
                  id: "object-opacity",
                  label: "Opacidad",
                  icon: "droplet",
                  min: 0.05,
                  max: 1,
                  step: 0.05,
                  decimals: 2,
                  get: (s) => s.selectedObject?.opacity ?? 1,
                  set: (v) => editor.setSelectedObject({ opacity: v }),
                },
                {
                  kind: "number",
                  id: "object-roughness",
                  label: "Rugosidad",
                  icon: "tune",
                  min: 0,
                  max: 1,
                  step: 0.05,
                  decimals: 2,
                  hint: "A 0, liso y brillante como un espejo; a 1, mate. Es lo que decide si la superficie refleja nítido o difuso.",
                  get: (s) => s.selectedObject?.roughness ?? 0.6,
                  set: (v) => editor.setSelectedObject({ roughness: v }),
                },
                {
                  kind: "number",
                  id: "object-metalness",
                  label: "Metalicidad",
                  icon: "tune",
                  min: 0,
                  max: 1,
                  step: 0.05,
                  decimals: 2,
                  hint: "A 0, un dieléctrico (plástico, yeso); a 1, metal. Un metal toma el color del reflejo, no del material.",
                  get: (s) => s.selectedObject?.metalness ?? 0,
                  set: (v) => editor.setSelectedObject({ metalness: v }),
                },
                {
                  kind: "number",
                  id: "object-emissive-intensity",
                  label: "Emisión",
                  icon: "spark",
                  min: 0,
                  max: 3,
                  step: 0.1,
                  decimals: 1,
                  hint: "Cuánto brilla el objeto por sí mismo, sin luz. A 0 no emite; sube para un material que ilumina (un cartel, una brasa).",
                  get: (s) => s.selectedObject?.emissiveIntensity ?? 0,
                  set: (v) => editor.setSelectedObject({ emissiveIntensity: v }),
                },
              ],
            },
          ],
        },
        {
          kind: "toggle",
          id: "occlude-strokes",
          label: "Trazos ocultos tras los objetos",
          icon: "cube",
          hint: "Encendido: un trazo pintado detrás de un objeto queda tapado por él, como en una escena real. Apagado: los trazos se dibujan siempre por encima, útil si usas el objeto solo como guía.",
          get: (s) => s.scene3dSettings.occludeStrokes,
          set: (v) => editor.setScene3D({ occludeStrokes: v }),
        },
        {
          kind: "number",
          id: "space-radius",
          label: "Radio del suavizado",
          icon: "circle",
          min: 2,
          max: 400,
          step: 1,
          unit: "u",
          decimals: 0,
          hint: "Zona que alcanza el suavizado. No es el tamano del pincel: se puede querer punta fina y mano ancha.",
          visible: (s) => s.scene3dSettings.tool === "smooth",
          get: (s) => s.scene3dSettings.radius,
          set: (v) => editor.setScene3D({ radius: v }),
        },
        {
          kind: "number",
          id: "space-strength",
          label: "Fuerza al suavizar",
          icon: "smooth",
          min: 0.02,
          max: 1,
          step: 0.01,
          decimals: 2,
          hint: "Cuanto relaja cada fotograma. Insistir sobre la misma zona suaviza mas.",
          visible: (s) => s.scene3dSettings.tool === "smooth",
          get: (s) => s.scene3dSettings.strength,
          set: (v) => editor.setScene3D({ strength: v }),
        },
        {
          kind: "toggle",
          id: "space-refine",
          label: "Mejorar al soltar",
          icon: "bake",
          hint: "Suaviza el trazo y limpia sus esquinas justo al cerrarlo. Va apagado: cambia lo que acabas de dibujar.",
          get: (s) => s.scene3dSettings.refineOnRelease,
          set: (v) => editor.setScene3D({ refineOnRelease: v }),
        },
        {
          kind: "number",
          id: "space-refine-strength",
          label: "Fuerza de la mejora",
          icon: "smooth",
          min: 0.05,
          max: 1,
          step: 0.05,
          decimals: 2,
          visible: (s) => s.scene3dSettings.refineOnRelease,
          get: (s) => s.scene3dSettings.refineStrength,
          set: (v) => editor.setScene3D({ refineStrength: v }),
        },
        {
          kind: "choice",
          id: "space-draw-plane",
          label: "Plano de dibujo",
          icon: "shape",
          chooser: "select",
          hint: "Sobre qué superficie cae el trazo. Hacia la cámara es el de siempre; Frente, Lado y Planta son los del mundo, para dibujar un alzado o una planta sin girar la cámara; Ancla pasa por el punto marcado.",
          options: DRAW_PLANE_ORDER.map((k) => ({
            value: k,
            id: `draw-plane-${k}`,
            label: DRAW_PLANE_LABELS[k],
          })),
          get: (s) => s.scene3dSettings.drawPlane,
          set: (v) => editor.setScene3D({ drawPlane: v as DrawPlaneKind }),
        },
        {
          kind: "choice",
          id: "space-placement",
          label: "Colocación",
          icon: "move",
          chooser: "select",
          hint: "A qué profundidad cae el trazo. En el plano es lo de siempre; Origen y Ancla lo fijan a una altura de referencia; Sobre el trazo lo pega a la profundidad del trazo que haya debajo del cursor.",
          options: PLACEMENT_ORDER.map((k) => ({
            value: k,
            id: `placement-${k}`,
            label: PLACEMENT_LABELS[k],
          })),
          get: (s) => s.scene3dSettings.placement.mode,
          set: (v) => editor.setSpacePlacement({ mode: v as StrokePlacement }),
        },
        {
          kind: "choice",
          id: "placement-target",
          label: "Pegar a",
          chooser: "select",
          hint: "Qué puntos de los trazos cuentan como superficie: todos, solo los extremos, o solo el primero (para abanicos desde un mismo arranque).",
          visible: (s) => s.scene3dSettings.placement.mode === "stroke",
          options: STROKE_TARGET_ORDER.map((k) => ({
            value: k,
            id: `placement-target-${k}`,
            label: STROKE_TARGET_LABELS[k],
          })),
          get: (s) => s.scene3dSettings.placement.target,
          set: (v) => editor.setSpacePlacement({ target: v as StrokeTarget }),
        },
        {
          kind: "number",
          id: "placement-radius",
          label: "Radio para pegar",
          icon: "circle",
          min: 2,
          max: 400,
          step: 1,
          unit: "u",
          decimals: 0,
          hint: "A qué distancia del cursor se busca un trazo al que pegarse. Fuera de él, el trazo cae en el plano.",
          visible: (s) => s.scene3dSettings.placement.mode === "stroke",
          get: (s) => s.scene3dSettings.placement.radius,
          set: (v) => editor.setSpacePlacement({ radius: v }),
        },
        {
          id: "space-continuation",
          label: "Continuación del trazo",
          icon: "select",
          hint: "De dónde toma el trazo su punto de arranque, hacia dónde sale y a qué está restringido. Son tres cosas independientes.",
          children: [
            {
              kind: "choice",
              id: "snap-source",
              label: "Arranca en",
              icon: "select",
              chooser: "segmented",
              hint: "Libre: donde apoyes. Trazos: se engancha al extremo de otro. Rejilla: al cruce del suelo. Anclaje: al punto que marques con Mayúsculas y clic.",
              options: SNAP_SOURCE_ORDER.map((k) => ({
                value: k,
                id: `snap-src-${k}`,
                label: SNAP_LABELS[k],
              })),
              get: (s) => s.scene3dSettings.snap.source,
              set: (v) => editor.setSpaceSnap({ source: v as SnapSource }),
            },
            {
              kind: "choice",
              id: "snap-pick",
              label: "A qué trazo",
              chooser: "select",
              hint: "El último dibujado, o el que tenga un extremo más cerca del puntero.",
              visible: (s) => s.scene3dSettings.snap.source === "strokes",
              options: (Object.keys(SNAP_PICK_LABELS) as SnapPick[]).map((k) => ({
                value: k,
                id: `snap-pick-${k}`,
                label: SNAP_PICK_LABELS[k],
              })),
              get: (s) => s.scene3dSettings.snap.pick,
              set: (v) => editor.setSpaceSnap({ pick: v as SnapPick }),
            },
            {
              kind: "number",
              id: "snap-radius",
              label: "Radio de captura",
              icon: "circle",
              min: 2,
              max: 400,
              step: 1,
              unit: "u",
              decimals: 0,
              hint: "A qué distancia del extremo se engancha. Fuera de él, el trazo sale libre.",
              visible: (s) => s.scene3dSettings.snap.source === "strokes",
              get: (s) => s.scene3dSettings.snap.radius,
              set: (v) => editor.setSpaceSnap({ radius: v }),
            },
            {
              kind: "toggle",
              id: "snap-tangent",
              label: "Continuidad de tangente",
              icon: "smooth",
              hint: "El trazo nuevo sale alineado con el anterior, para que la unión no haga pico, y se suelta a lo largo del alcance.",
              visible: (s) => s.scene3dSettings.snap.source === "strokes",
              get: (s) => s.scene3dSettings.snap.tangent,
              set: (v) => editor.setSpaceSnap({ tangent: v }),
            },
            {
              kind: "number",
              id: "snap-blend",
              label: "Alcance de la tangente",
              icon: "smooth",
              min: 5,
              max: 400,
              step: 5,
              unit: "u",
              decimals: 0,
              hint: "Cuánto tarda el trazo en soltarse de la dirección heredada. Corto = se dobla enseguida.",
              visible: (s) =>
                s.scene3dSettings.snap.source === "strokes" && s.scene3dSettings.snap.tangent,
              get: (s) => s.scene3dSettings.snap.tangentBlend,
              set: (v) => editor.setSpaceSnap({ tangentBlend: v }),
            },
            {
              kind: "choice",
              id: "snap-axis",
              label: "Restringir a",
              icon: "move",
              chooser: "select",
              hint: "Los ejes de la vista son los de la cámara ahora: al orbitar dejan de coincidir con los del mundo. Mantén Mayúsculas al arrastrar para forzar el eje horizontal.",
              options: SNAP_AXIS_ORDER.map((k) => ({
                value: k,
                id: `snap-axis-${k}`,
                label: SNAP_AXIS_LABELS[k],
              })),
              get: (s) => s.scene3dSettings.snap.axis,
              set: (v) => editor.setSpaceSnap({ axis: v as SnapAxis }),
            },
            {
              kind: "action",
              id: "snap-anchor-clear",
              label: "Quitar anclaje",
              icon: "close",
              disabled: (s) => s.scene3dSettings.snap.anchor === null,
              run: () => editor.setSpaceSnap({ anchor: null }),
            },
          ],
        },
        {
          id: "space-floor",
          label: "Suelo",
          icon: "grid",
          hint: "La rejilla da referencia espacial: sin ella, en el aire, no hay forma de saber a qué distancia ni a qué altura se está dibujando.",
          children: [
            {
              kind: "toggle",
              id: "grid-on",
              label: "Rejilla del suelo",
              icon: "grid",
              get: (s) => s.scene3dSettings.grid.enabled,
              set: (v) => editor.setScene3D({ grid: { ...editor.state.scene3dSettings.grid, enabled: v } }),
            },
            {
              kind: "number",
              id: "grid-step",
              label: "Paso",
              icon: "grid",
              min: 5,
              max: 500,
              step: 5,
              unit: "u",
              decimals: 0,
              // El paso es UNO solo: el de la rejilla que se ve es el mismo al que
              // se engancha el trazo. Dos numeros distintos para lo mismo acabarian
              // enseñando una rejilla a la que el trazo no se pega.
              hint: "Lado de cada cuadro. Es también el paso al que se engancha el trazo cuando el arranque está en Rejilla.",
              visible: (s) =>
                s.scene3dSettings.grid.enabled || s.scene3dSettings.snap.source === "grid",
              get: (s) => s.scene3dSettings.snap.gridStep,
              set: (v) => editor.setSpaceSnap({ gridStep: v }),
            },
            {
              kind: "number",
              id: "grid-opacity",
              label: "Opacidad de la rejilla",
              icon: "droplet",
              min: 0.05,
              max: 1,
              step: 0.05,
              decimals: 2,
              visible: (s) => s.scene3dSettings.grid.enabled,
              get: (s) => s.scene3dSettings.grid.opacity,
              set: (v) => editor.setScene3D({ grid: { ...editor.state.scene3dSettings.grid, opacity: v } }),
            },
          ],
        },
        {
          id: "space-shadow",
          label: "Sombra",
          icon: "droplet",
          hint: "Sombra real de los objetos sobre el suelo: cada objeto proyecta su forma con la luz principal. Necesita la rejilla puesta, que hace de suelo.",
          children: [
            {
              kind: "toggle",
              id: "shadow-on",
              label: "Sombra en el suelo",
              icon: "droplet",
              get: (s) => s.scene3dSettings.shadow.enabled,
              set: (v) => editor.setScene3D({ shadow: { ...editor.state.scene3dSettings.shadow, enabled: v } }),
            },
            {
              kind: "number",
              id: "shadow-opacity",
              label: "Opacidad de la sombra",
              icon: "droplet",
              min: 0,
              max: 1,
              step: 0.05,
              decimals: 2,
              hint: "Cuanto oscurece la sombra el suelo. A 0 no se ve; a 1, muy marcada.",
              visible: (s) => s.scene3dSettings.shadow.enabled,
              get: (s) => s.scene3dSettings.shadow.opacity,
              set: (v) => editor.setScene3D({ shadow: { ...editor.state.scene3dSettings.shadow, opacity: v } }),
            },
            {
              kind: "number",
              id: "shadow-softness",
              label: "Suavidad del borde",
              icon: "tune",
              min: 0,
              max: 1,
              step: 0.05,
              decimals: 2,
              hint: "A 0, el borde de la sombra es neto; subiendo, se desenfoca (penumbra), como una sombra lejana o una luz difusa.",
              visible: (s) => s.scene3dSettings.shadow.enabled,
              get: (s) => s.scene3dSettings.shadow.softness,
              set: (v) => editor.setScene3D({ shadow: { ...editor.state.scene3dSettings.shadow, softness: v } }),
            },
            {
              kind: "swatches",
              id: "shadow-color",
              label: "Color de la sombra",
              icon: "palette",
              hint: "La sombra no tiene por qué ser negra: un tinte frío (azulado) o cálido la integra mejor con la luz de la escena.",
              visible: (s) => s.scene3dSettings.shadow.enabled,
              // Negro neutro y unos tintes frios/calidos tipicos de sombra.
              colors: () => ["#000000", "#1a2340", "#241a40", "#0e2436", "#3a2418", "#2a2a2a"],
              value: (s) => s.scene3dSettings.shadow.color,
              onPick: (hex) => editor.setScene3D({ shadow: { ...editor.state.scene3dSettings.shadow, color: hex } }),
            },
            {
              kind: "number",
              id: "shadow-extent",
              label: "Alcance de la sombra",
              icon: "fit",
              min: 300,
              max: 4000,
              step: 100,
              unit: "u",
              decimals: 0,
              hint: "Hasta dónde llega el mapa de sombra. Poco alcance da un borde más fino en una escena pequeña; súbelo si la sombra se corta en una escena grande.",
              visible: (s) => s.scene3dSettings.shadow.enabled,
              get: (s) => s.scene3dSettings.shadow.extent,
              set: (v) => editor.setScene3D({ shadow: { ...editor.state.scene3dSettings.shadow, extent: v } }),
            },
            {
              kind: "choice",
              id: "shadow-resolution",
              label: "Nitidez de la sombra",
              icon: "grid",
              chooser: "segmented",
              hint: "Tamaño del mapa de sombra. Más nitidez = borde más limpio y detalle fino, a cambio de memoria y coste.",
              visible: (s) => s.scene3dSettings.shadow.enabled,
              options: [
                { value: "1024", id: "shadow-res-1024", label: "Media" },
                { value: "2048", id: "shadow-res-2048", label: "Alta" },
                { value: "4096", id: "shadow-res-4096", label: "Máxima" },
              ],
              get: (s) => String(s.scene3dSettings.shadow.resolution),
              set: (v) => editor.setScene3D({ shadow: { ...editor.state.scene3dSettings.shadow, resolution: Number(v) } }),
            },
            {
              kind: "toggle",
              id: "shadow-strokes",
              label: "Los trazos proyectan sombra",
              icon: "brush",
              hint: "Cada trazo tira su sombra con su forma sobre el suelo, los objetos y los demás trazos. Es más costoso: enciéndelo cuando quieras que el dibujo del espacio ensombrezca la escena, no solo los objetos.",
              visible: (s) => s.scene3dSettings.shadow.enabled,
              get: (s) => s.scene3dSettings.strokeShadow,
              set: (v) => editor.setScene3D({ strokeShadow: v }),
            },
            {
              kind: "toggle",
              id: "shadow-strokes-receive",
              label: "Los trazos reciben sombra",
              icon: "brush",
              hint: "Un trazo que queda bajo la sombra de un objeto o de otro trazo se oscurece. Es el complemento de proyectar: enciéndelo para que el dibujo se integre en la escena en vez de leerse plano por encima.",
              visible: (s) => s.scene3dSettings.shadow.enabled,
              get: (s) => s.scene3dSettings.strokeReceiveShadow,
              set: (v) => editor.setScene3D({ strokeReceiveShadow: v }),
            },
          ],
        },
        {
          id: "space-lights",
          label: "Luces",
          icon: "spark",
          hint: "La luz decide cómo se lee la materia: sin ella, un trazo es una silueta plana. De momento ilumina; las sombras proyectadas sobre el suelo van aparte.",
          children: [
            {
              kind: "toggle",
              id: "lights-on",
              label: "Iluminar",
              icon: "spark",
              disabled: (s) => s.lights.count === 0,
              get: (s) => s.lights.enabled,
              set: (v) => editor.setLightsEnabled(v),
            },
            {
              kind: "number",
              id: "light-intensity",
              label: "Intensidad de la luz",
              icon: "spark",
              min: 0,
              max: 2,
              step: 0.05,
              decimals: 2,
              visible: (s) => s.lights.key !== null && s.lights.key.enabled,
              get: (s) => s.lights.key?.intensity ?? 1,
              set: (v) => editor.setKeyLight({ intensity: v }),
            },
            {
              // Los tres ejes de la direccion, como deslizadores. Es menos bonito
              // que una bola para girar la luz, pero dice exactamente donde esta
              // -y el numero es lo que hace falta cuando se busca una sombra
              // concreta sobre el suelo-.
              id: "light-dir",
              label: "Dirección",
              icon: "move",
              visible: (s) => s.lights.key !== null && s.lights.key.enabled,
              children: [
                {
                  kind: "number",
                  id: "light-x",
                  label: "Lado",
                  min: -1,
                  max: 1,
                  step: 0.05,
                  decimals: 2,
                  get: (s) => s.lights.key?.x ?? 0,
                  set: (v) => editor.setKeyLight({ x: v }),
                },
                {
                  kind: "number",
                  id: "light-y",
                  label: "Altura",
                  min: -1,
                  max: 1,
                  step: 0.05,
                  decimals: 2,
                  hint: "Positivo: la luz está por encima; las sombras caen hacia el suelo.",
                  get: (s) => s.lights.key?.y ?? 0,
                  set: (v) => editor.setKeyLight({ y: v }),
                },
                {
                  kind: "number",
                  id: "light-z",
                  label: "Frente",
                  min: -1,
                  max: 1,
                  step: 0.05,
                  decimals: 2,
                  get: (s) => s.lights.key?.z ?? 0,
                  set: (v) => editor.setKeyLight({ z: v }),
                },
              ],
            },
            {
              kind: "number",
              id: "ambient-intensity",
              label: "Luz ambiente",
              icon: "droplet",
              min: 0,
              max: 2,
              step: 0.05,
              decimals: 2,
              hint: "Lo que se ve donde no llega ninguna luz. A cero, lo que queda a la sombra se vuelve negro.",
              visible: (s) => s.lights.ambient !== null && s.lights.ambient.enabled,
              get: (s) => s.lights.ambient?.intensity ?? 0,
              set: (v) => editor.setAmbientLight({ intensity: v }),
            },
            {
              kind: "action",
              id: "light-from-camera",
              label: "Luz desde la cámara",
              icon: "fit",
              hint: "Coloca la luz principal sobre el hombro de la cámara, que es donde menos sombras raras hace.",
              run: () => editor.lightFromCamera(),
            },
          ],
        },
        { kind: "action", id: "space-frame", label: "Encuadrar", icon: "fit", run: () => editor.frame3D() },
        {
          kind: "action",
          id: "space-clear",
          label: "Vaciar el espacio",
          icon: "trash",
          danger: true,
          disabled: (s) => s.scene3d.strokes === 0 && s.scene3d.fills === 0,
          run: () => editor.clearScene3D(),
        },
      ],
    },
    { kind: "toggle", id: "invert-erase", label: "Usar como borrador", icon: "eraser", hint: "Invierte el trazo, relleno o arrastre a borrado: el mismo gesto recorta la tinta. Tecla Alt.", visible: (s) => s.brush.mode !== "erase" && !s.brush.asMatter, get: (s) => s.brush.invertErase, set: (v) => editor.setBrush({ invertErase: v }) },
    // ---- Opciones exclusivas del Borrador (visibles solo en modo "erase"). ----
    {
      kind: "choice",
      id: "erase-mode",
      label: "Borrado",
      icon: "eraser",
      chooser: "segmented",
      hint: "Pincel recorta a mano; Forma usa la forma activa; Objeto borra el trazo tocado; Color borra todos los de ese color.",
      visible: (s) => s.brush.mode === "erase",
      options: (Object.keys(ERASE_MODE_LABELS) as EraseMode[]).map((k) => ({ value: k, id: `erase-${k}`, label: ERASE_MODE_LABELS[k] })),
      get: (s) => s.brush.eraseMode,
      set: (v) => editor.setBrush({ eraseMode: v as EraseMode }),
    },
    { kind: "toggle", id: "erase-fade", label: "Difuminar", icon: "spark", hint: "En vez de borrar del todo, aclara segun la opacidad (borrado suave).", visible: (s) => s.brush.mode === "erase", get: (s) => s.brush.eraseFade, set: (v) => editor.setBrush({ eraseFade: v }) },
    { kind: "toggle", id: "erase-matter", label: "Incluir materia", icon: "matter", hint: "El mismo gesto elimina tambien los cuerpos de materia que toque.", visible: (s) => s.brush.mode === "erase", get: (s) => s.brush.eraseMatter, set: (v) => editor.setBrush({ eraseMatter: v }) },
    {
      kind: "choice",
      id: "pull-family",
      label: "Familia",
      icon: "matter",
      chooser: "select",
      hint: "Arrastra para estirar una forma entre los dos puntos.",
      visible: (s) => s.brush.mode === "pull",
      options: (Object.keys(PULL_LABELS) as (PullFamily | "random")[]).map((k) => ({ value: k, id: `pull-${k}`, label: PULL_LABELS[k] })),
      get: (s) => s.pullFamily,
      set: (v) => editor.setPullFamily(v as PullFamily | "random"),
    },
    {
      kind: "action",
      id: "brush-reset",
      label: "Restablecer pincel por defecto",
      icon: "reset",
      hint: "Vuelve todos los ajustes del pincel a como venían al principio (tamaño, dinámica, raster, 3D no toca).",
      run: () => {
        if (confirm("¿Restablecer el pincel a sus valores por defecto?")) {
          editor.setBrush({ ...DEFAULT_BRUSH });
          editor.status("Pincel restablecido");
        }
      },
    },
  ];

  const brush: Domain = {
    id: "brush",
    label: "Pincel",
    icon: "brush",
    layout: "brush-by-mode",
    brushByMode: {
      modes: (["stroke", "fill", "pull", "erase"] as BrushMode[]).map((m) => ({ value: m, label: MODE_LABELS[m], icon: MODE_ICONS[m], title: `${MODE_LABELS[m]} (${MODE_KEYS[m]})` })),
      current: (s) => s.brush.mode,
      isToolActive: (s) => s.tool === "brush",
      activate: (m) => { editor.setTool("brush"); editor.setBrush({ mode: m }); },
      select: (m) => editor.setBrush({ mode: m }),
    },
    children: brushOptions,
  };
  // -------------------------------------------------------------- Color
  const color: Domain = {
    id: "color",
    label: "Color",
    icon: "droplet",
    accent: (s) => s.color,
    children: [
      // Selector HSV: exclusivo del dock. La rueda del radial cumple ese papel.
      {
        kind: "custom",
        id: "color-picker",
        label: "Color",
        surfaces: ["dock"],
        // renderDock se cablea en el adaptador del dock (necesita cruzar su
        // sincronía con las muestras); aquí solo se declara su lugar.
      },
      { kind: "action", id: "wheel", label: "Rueda", icon: "wheel", surfaces: ["radial"], run: () => hooks.toggleWheel() },
      toolAction("picker", editor),
      // Paletas fijas: submenú de paletas → colores en el radial; pestañas +
      // pozos en el dock. Elegir un color marca la paleta y aplica el color.
      {
        kind: "custom",
        id: "palette",
        label: "Paleta",
        icon: "palette",
        renderRadial: (s): HotNode => ({
          kind: "submenu",
          id: "palette",
          label: "Paleta",
          icon: "palette",
          children: DEFAULT_PALETTES.map((p, i) => ({
            kind: "submenu" as const,
            id: `palette-${i}`,
            label: p.name,
            active: s.paletteIndex === i,
            children: p.colors.map((hex, j) => ({
              kind: "action" as const,
              id: `palette-${i}-${j}`,
              label: hex.toUpperCase(),
              accent: hex,
              active: hex.toLowerCase() === s.color.toLowerCase(),
              keepOpen: true,
              run: () => { editor.setPalette(i); editor.setColor(hex); },
            })),
          })),
        }),
      },
      // Últimos colores usados: acciones sueltas en el radial; muestras en el dock.
      {
        kind: "custom",
        id: "recents",
        label: "Recientes",
        renderRadial: (s): HotNode[] => s.recentColors.map((hex, i) => ({
          kind: "action" as const,
          id: `swatch-${i}`,
          label: hex.toUpperCase(),
          accent: hex,
          active: hex.toLowerCase() === s.color.toLowerCase(),
          keepOpen: true,
          run: () => editor.setColor(hex),
        })),
      },
    ],
  };
  // ------------------------------------------------------------ Materia
  // Ajustes de la forma activa: solo entran los diales que esa forma usa de
  // verdad (poda por tipo). El submenú `shape-cfg` y esta poda los verifica
  // `scripts/smoke-ui.ts`, así que ids, etiquetas y criterio se conservan.
  const shapeCfg: Group = {
    id: "shape-cfg",
    label: "Forma",
    icon: "shape",
    children: [
      {
        kind: "choice",
        id: "shape-kind",
        label: "Tipo",
        chooser: "segmented",
        options: (Object.keys(SHAPE_LABELS) as ShapeKind[]).map((k) => ({ value: k, id: `shape-${k}`, label: SHAPE_LABELS[k] })),
        get: (s) => s.shape.kind,
        set: (v) => editor.setShape({ kind: v as ShapeKind }),
      },
      { kind: "number", id: "shape-size", label: "Tamano", min: 4, max: 300, step: 1, gamma: 1.6, unit: "px", get: (s) => s.shape.size, set: (v) => editor.setShape({ size: v }) },
      { kind: "number", id: "shape-aspect", label: "Proporcion", min: 0.25, max: 4, step: 0.05, decimals: 2, visible: (s) => s.shape.kind === "box" || s.shape.kind === "capsule", get: (s) => s.shape.aspect, set: (v) => editor.setShape({ aspect: v }) },
      { kind: "number", id: "shape-sides", label: "Lados", min: 3, max: 12, step: 1, visible: (s) => s.shape.kind === "ngon" || s.shape.kind === "star", get: (s) => s.shape.sides, set: (v) => editor.setShape({ sides: Math.round(v) }) },
      { kind: "number", id: "shape-inner", label: "Radio interior", min: 0.15, max: 0.9, step: 0.01, decimals: 2, visible: (s) => s.shape.kind === "star", get: (s) => s.shape.inner, set: (v) => editor.setShape({ inner: v }) },
      { kind: "number", id: "shape-round", label: "Redondeo", min: 0, max: 60, step: 0.5, decimals: 1, unit: "px", visible: (s) => s.shape.kind === "box" || s.shape.kind === "ngon", get: (s) => s.shape.round, set: (v) => editor.setShape({ round: v }) },
      { kind: "custom", id: "note-shape", label: "Nota", surfaces: ["dock"], hint: "Arrastra en el lienzo para colocarla y girarla antes de soltarla." },
    ],
  };

  const physicsCfg: Group = {
    id: "physics-cfg",
    label: "Física",
    icon: "tune",
    children: [
      { kind: "action", id: "sim-toggle", label: (s) => (s.running ? "Pausar" : "Reanudar"), icon: (s) => (s.running ? "pause" : "play"), hint: (s) => (s.running ? "Pausar toda la simulación (física y puentes orgánicos)" : "Reanudar la simulación"), run: () => editor.setRunning(!editor.state.running), toggled: (s) => s.running },
      { kind: "number", id: "gravity", label: "Gravedad", min: -2000, max: 2000, step: 10, get: (s) => s.world.gravity.y, set: (v) => editor.setWorld({ gravity: { x: editor.state.world.gravity.x, y: v } }) },
      { kind: "number", id: "gravity-x", label: "Gravedad lateral", min: -2000, max: 2000, step: 10, get: (s) => s.world.gravity.x, set: (v) => editor.setWorld({ gravity: { x: v, y: editor.state.world.gravity.y } }) },
      { kind: "number", id: "cohesion", label: "Cohesion", min: 0, max: 1, step: 0.01, decimals: 2, hint: "Atraccion mutua: las formas se buscan y se funden entre si.", get: (s) => s.world.cohesion, set: (v) => editor.setWorld({ cohesion: v }) },
      { kind: "number", id: "cohesion-distance", label: "Alcance de conexion", min: 0, max: 600, step: 5, gamma: 1.5, unit: "px", hint: "Distancia maxima de la atraccion entre cuerpos. 0 = automatico segun sus radios.", get: (s) => s.world.cohesionDistance, set: (v) => editor.setWorld({ cohesionDistance: v }) },
      { kind: "number", id: "damping", label: "Rozamiento del aire", min: 0, max: 3, step: 0.01, decimals: 2, get: (s) => s.world.damping, set: (v) => editor.setWorld({ damping: v }) },
      { kind: "number", id: "restitution", label: "Rebote", min: 0, max: 1, step: 0.01, decimals: 2, get: (s) => s.world.restitution, set: (v) => editor.setWorld({ restitution: v }) },
      { kind: "number", id: "friction", label: "Friccion", min: 0, max: 1.5, step: 0.01, decimals: 2, get: (s) => s.world.friction, set: (v) => editor.setWorld({ friction: v }) },
      { kind: "number", id: "iterations", label: "Precision", min: 1, max: 24, step: 1, hint: "Iteraciones del solver: mas alto agarra mejor las pilas, cuesta mas.", get: (s) => s.world.iterations, set: (v) => editor.setWorld({ iterations: Math.round(v) }) },
      { kind: "number", id: "time-scale", label: "Velocidad del tiempo", min: 0.05, max: 3, step: 0.05, decimals: 2, get: (s) => s.world.timeScale, set: (v) => editor.setWorld({ timeScale: v }) },
      { kind: "toggle", id: "sleeping", label: "Dormir en reposo", icon: "spark", hint: "Los cuerpos quietos dejan de calcularse hasta que algo los toca.", get: (s) => s.world.sleeping, set: (v) => editor.setWorld({ sleeping: v }) },
      { kind: "toggle", id: "colliders", label: "Ver colisionadores", icon: "grid", hint: "Dibuja la forma real con la que choca cada cuerpo.", get: (s) => s.debugColliders, set: () => editor.toggleColliders() },
      { kind: "toggle", id: "bridge-reach-view", label: "Ver alcance de puentes", icon: "grid", hint: "Dibuja el radio hasta donde cada cuerpo tiende puentes (verde = propio, azul = global).", get: (s) => s.showBridgeReach, set: () => editor.toggleBridgeReach() },
      { kind: "action", id: "zero-g", label: "Cero G", icon: "spark", run: () => editor.setWorld({ gravity: { x: 0, y: 0 } }) },
    ],
  };

  const fieldCfg: Group = {
    id: "field-cfg",
    label: "Acabado",
    icon: "layers",
    children: [
      { kind: "number", id: "blend", label: "Fusion", min: 0, max: 160, step: 1, gamma: 1.5, unit: "px", hint: "Radio de mezcla: a mas alto, las formas se funden antes de tocarse.", get: (s) => s.field.blend, set: (v) => editor.setField({ blend: v }) },
      { kind: "number", id: "bridge-reach", label: "Puentes", min: 0, max: 600, step: 5, gamma: 1.5, unit: "px", hint: "Alcance global de los puentes entre formas. No atrae los cuerpos: solo tiende cuellos entre vecinos dentro de ese radio. Con la herramienta Puente (P) se ajusta cuerpo a cuerpo.", get: (s) => s.field.bridgeReach, set: (v) => editor.setField({ bridgeReach: v }) },
      { kind: "choice", id: "bridge-style", label: "Forma del puente", chooser: "segmented", hint: "Recto: cuello limpio. Desgarrado: puente discontinuo, como roto. Organico: ondula y se mueve.", options: [
        { value: "direct", id: "bridge-direct", label: "Recto" },
        { value: "torn", id: "bridge-torn", label: "Desgarrado" },
        { value: "organic", id: "bridge-organic", label: "Organico" },
      ], visible: (s) => s.field.bridgeReach > 0, get: (s) => s.field.bridgeStyle, set: (v) => editor.setField({ bridgeStyle: v as BridgeStyle }) },
      { kind: "number", id: "bridge-threads", label: "Hilos", min: 1, max: 6, step: 1, hint: "Talla cortes finos en la union, de modo que el puente se vea separado en hebras justo donde se conecta con cada cuerpo.", visible: (s) => s.field.bridgeReach > 0, get: (s) => s.field.bridgeThreads, set: (v) => editor.setField({ bridgeThreads: Math.round(v) }) },
      { kind: "number", id: "bridge-thread-reach", label: "Alcance hilos", min: 0.05, max: 1, step: 0.01, decimals: 2, hint: "Hasta donde llegan los cortes desde la conexion: bajo = solo al principio y se funden pronto hacia el centro; alto = casi todo el puente.", visible: (s) => s.field.bridgeReach > 0 && s.field.bridgeThreads > 1, get: (s) => s.field.bridgeThreadReach, set: (v) => editor.setField({ bridgeThreadReach: v }) },
      { kind: "number", id: "bridge-flare", label: "Conexion", min: 0, max: 1, step: 0.01, decimals: 2, hint: "Ensancha el puente donde se conecta con los cuerpos (entradas y salidas mas gruesas que el centro).", visible: (s) => s.field.bridgeReach > 0, get: (s) => s.field.bridgeFlare, set: (v) => editor.setField({ bridgeFlare: v }) },
      { kind: "toggle", id: "bridge-fade", label: "Difuminar por alcance", icon: "layers", hint: "Atenua la opacidad del puente hasta el borde del area de alcance: tenue de lejos, mas opaco al acercarse. Apagado = puentes solidos y completos.", visible: (s) => s.field.bridgeReach > 0, get: (s) => s.field.bridgeFade, set: () => editor.setField({ bridgeFade: !editor.state.field.bridgeFade }) },
      { kind: "number", id: "outline", label: "Contorno", min: 0, max: 12, step: 0.5, decimals: 1, unit: "px", get: (s) => s.field.outline, set: (v) => editor.setField({ outline: v }) },
      { kind: "number", id: "shade", label: "Volumen", min: 0, max: 1, step: 0.01, decimals: 2, get: (s) => s.field.shade, set: (v) => editor.setField({ shade: v }) },
      { kind: "number", id: "gloss", label: "Brillo", min: 0, max: 1.5, step: 0.01, decimals: 2, get: (s) => s.field.gloss, set: (v) => editor.setField({ gloss: v }) },
      { kind: "number", id: "depth", label: "Profundidad", min: 4, max: 160, step: 1, gamma: 1.4, unit: "px", hint: "Grosor aparente de la cupula de cada masa.", get: (s) => s.field.depth, set: (v) => editor.setField({ depth: v }) },
      { kind: "number", id: "field-alpha", label: "Opacidad", min: 0.05, max: 1, step: 0.01, decimals: 2, get: (s) => s.field.alpha, set: (v) => editor.setField({ alpha: v }) },
    ],
  };

  const matter: Domain = {
    id: "matter-cfg",
    label: "Materia",
    icon: "matter",
    children: [
      toolAction("shape", editor),
      toolAction("matter", editor),
      {
        kind: "choice",
        id: "matter-op",
        label: "Transformar",
        icon: "move",
        chooser: "segmented",
        hint: "Mover: arrastra el cuerpo o el trazo entero. Rotar: gira alrededor del pivote. Escalar: aleja o acerca al pivote. Pivote: coloca a mano el punto de giro/escala; se mantiene coherente al transformar y con la física.",
        visible: (s) => s.tool === "matter",
        options: [
          { value: "move", id: "matter-op-move", label: "Mover" },
          { value: "rotate", id: "matter-op-rotate", label: "Rotar" },
          { value: "scale", id: "matter-op-scale", label: "Escalar" },
          { value: "pivot", id: "matter-op-pivot", label: "Pivote" },
        ],
        get: (s) => s.matterOp,
        set: (v) => editor.setMatterOp(v as MatterOp),
      },
      toolAction("bridge", editor),
      { kind: "action", id: "run", label: (s) => (s.running ? "Pausar" : "Reanudar"), icon: (s) => (s.running ? "pause" : "play"), surfaces: ["radial"], keepOpen: true, toggled: (s) => s.running, run: () => editor.setRunning(!editor.state.running) },
      { kind: "action", id: "seed", label: "Sembrar", icon: "seed", surfaces: ["radial"], run: () => editor.seedMatter(8) },
      { kind: "action", id: "bake", label: "Hornear", icon: "bake", surfaces: ["radial"], run: () => editor.bakeMatter() },
      { kind: "action", id: "ink-to-matter", label: "Tinta a materia", icon: "matter", disabled: (s) => {
        const layer = s.layers.find((l) => l.id === s.activeLayerId);
        return layer?.kind !== "ink" || !editor.doc.layerItems(s.activeLayerId).some((item) => !item.erase);
      }, run: () => editor.convertInkToMatter() },
      shapeCfg,
      physicsCfg,
      fieldCfg,
      // La conversión se activa directamente desde el modo Materia del pincel.
      { kind: "toggle", id: "walls", label: "Paredes", icon: "grid", get: (s) => s.showWalls, set: () => editor.toggleWalls() },
      { kind: "action", id: "clear-matter", label: "Vaciar", icon: "trash", accent: "#ff5f6d", danger: true, run: () => editor.clearMatter() },
    ],
  };
  // ------------------------------------------------------------ Archivo
  const file: Domain = {
    id: "file",
    label: "Archivo",
    icon: "file",
    surfaces: ["radial"],
    children: [
      { kind: "action", id: "undo", label: "Deshacer", icon: "undo", disabled: (s) => !s.history.canUndo, run: () => editor.undo() },
      { kind: "action", id: "redo", label: "Rehacer", icon: "redo", disabled: (s) => !s.history.canRedo, run: () => editor.redo() },
      { kind: "action", id: "new", label: "Nuevo", icon: "trash", run: () => hooks.newDoc() },
      { kind: "action", id: "open", label: "Abrir", icon: "folder", run: () => hooks.openFile() },
      { kind: "action", id: "import", label: "Importar imagen", icon: "image", hint: "Importa JPG, PNG, WebP o PSD por capas. También puedes arrastrar o pegar (Ctrl+V).", run: () => hooks.importImage() },
      { kind: "action", id: "save", label: "Guardar", icon: "save", run: () => hooks.save() },
      { kind: "action", id: "save-as", label: "Guardar como", icon: "save", hint: "Elige otro archivo. El documento pasa a guardarse en él.", run: () => hooks.saveAs() },
      { kind: "action", id: "png", label: "PNG", icon: "download", run: () => hooks.exportPng() },
      { kind: "action", id: "svg", label: "SVG", icon: "download", run: () => hooks.exportSvg() },
      { kind: "action", id: "help", label: "Atajos", icon: "info", run: () => hooks.help() },
    ],
  };

  // ----------------------------------------------------------- Simetria
  // Panel unificado segun captura: toggle arriba → presets 0/45/90 → angulo →
  // tipo (Radial/Caleidoscopio) → sectores → centrar/enderezar → guia/bloquear.
  // S activa/desactiva recordando el ultimo tipo; presets 0/45/90 son un click.
  // El segmentado solo ofrece Radial y Caleidoscopio: al activar la simetria ya
  // esta en Espejo (modo base), asi que estos dos botones "suben" a un modo con
  // sectores. Clic en el ya activo vuelve a Espejo (ningun boton resaltado).
  const ACTIVE_SYMS: SymmetryMode[] = ["radial", "kaleido"];
  const ACTIVE_LABELS: Record<string, string> = { mirror: "Espejo", radial: "Radial", kaleido: "Caleidoscopio" };
  const symmetry: Domain = {
    id: "symmetry",
    label: "Simetria",
    icon: "symmetry",
    children: [
      toolAction("symmetry", editor),
      {
        kind: "toggle",
        id: "sym-active",
        label: "Simetria",
        icon: "symmetry",
        hint: (s) => s.symmetry.mode === "none" ? "Activar simetria (S) — vuelve al ultimo tipo usado" : `${SYMMETRY_LABELS[s.symmetry.mode]} activa — S para desactivar`,
        get: (s) => s.symmetry.mode !== "none",
        set: (v) => {
          const cur = editor.state.symmetry;
          if (v && cur.mode === "none") editor.setSymmetry({ mode: cur.lastMode });
          else if (!v && cur.mode !== "none") editor.setSymmetry({ mode: "none" });
        },
      },
      // Presets rapidos: 0 / 45 / 90 como en la captura (radios sutiles sobre el angulo)
      { kind: "custom", id: "sym-angle-presets", label: "Angulo", surfaces: ["dock"] },
      { kind: "number", id: "sym-angle", label: "Angulo", min: -180, max: 180, step: 1, unit: "deg", visible: (s) => s.symmetry.mode !== "none", whenHidden: "dim", get: (s) => (s.symmetry.angle * 180) / Math.PI, set: (v) => editor.setSymmetry({ angle: (v * Math.PI) / 180 }) },
      // Tipo: Radial / Caleidoscopio. Espejo es el modo base (al activar la
      // simetria). Clic en Radial/Caleidoscopio sube a ese modo; clic en el que
      // ya esta activo baja de vuelta a Espejo (ningun boton resaltado).
      {
        kind: "choice",
        id: "sym-type",
        label: "Tipo",
        icon: "symmetry",
        chooser: "segmented",
        visible: (s) => s.symmetry.mode !== "none",
        whenHidden: "prune",
        options: ACTIVE_SYMS.map((k) => ({ value: k, id: `sym-${k}`, label: ACTIVE_LABELS[k] })),
        get: (s) => (s.symmetry.mode === "none" ? s.symmetry.lastMode : s.symmetry.mode),
        set: (v) => {
          const cur = editor.state.symmetry;
          const next = cur.mode === v ? "mirror" : (v as SymmetryMode);
          if (cur.mode === "none") {
            editor.doc.symmetry.lastMode = next as unknown as typeof editor.doc.symmetry.lastMode;
            editor.emitState();
          } else {
            editor.setSymmetry({ mode: next });
          }
        },
      },
      // Sectores: solo cuando Radial o Caleidoscopio esta activo (Espejo no tiene
      // sectores). Se oculta por completo en Espejo o con la simetria apagada.
      { kind: "number", id: "sym-count", label: "Sectores", min: 2, max: 64, step: 1, gamma: 1.4, visible: (s) => s.symmetry.mode === "radial" || s.symmetry.mode === "kaleido", whenHidden: "prune", get: (s) => s.symmetry.count, set: (v) => editor.setSymmetry({ count: Math.round(v) }) },
      { kind: "action", id: "sym-center", label: "Centrar", visible: (s) => s.symmetry.mode !== "none", whenHidden: "dim", run: () => editor.setSymmetry({ x: editor.camera.x, y: editor.camera.y }) },
      { kind: "action", id: "sym-straighten", label: "Enderezar", visible: (s) => s.symmetry.mode !== "none", whenHidden: "dim", run: () => editor.setSymmetry({ angle: 0 }) },
      { kind: "toggle", id: "sym-visible", label: "Guia", hint: "Muestra el eje sobre el lienzo.", visible: (s) => s.symmetry.mode !== "none", whenHidden: "dim", get: (s) => s.symmetry.visible, set: (v) => editor.setSymmetry({ visible: v }) },
      { kind: "toggle", id: "sym-locked", label: "Bloquear", hint: "Evita mover el eje sin querer mientras dibujas.", visible: (s) => s.symmetry.mode !== "none", whenHidden: "dim", get: (s) => s.symmetry.locked, set: (v) => editor.setSymmetry({ locked: v }) },
      // Compat: mantiene el id antiguo para deep-links/chips viejos — espeja sym-type
      {
        kind: "choice",
        id: "sym-mode",
        label: "Modo",
        chooser: "select",
        surfaces: [],
        options: (Object.keys(SYMMETRY_LABELS) as SymmetryMode[]).map((k) => ({ value: k, id: `sym-${k}-legacy`, label: SYMMETRY_LABELS[k] })),
        get: (s) => s.symmetry.mode,
        set: (v) => editor.setSymmetry({ mode: v as SymmetryMode }),
      },
      { kind: "custom", id: "sym-copies", label: "Copias", surfaces: ["dock"] },
      { kind: "custom", id: "note-symmetry", label: "Nota", surfaces: ["dock"], hint: "S activa/desactiva la simetria recordando el ultimo tipo. Con S+Shift o la herramienta Simetria arrastras el eje a cualquier punto." },
      // ROTACION de lienzo — vive como seccion inferior del mismo panel (vista vs
      // contenido, estilo Photoshop). Es un grupo propio para que salga bajo su
      // propia cabecera "Rotacion" dentro de la pagina de Simetria.
      {
        id: "rotation",
        label: "Rotacion",
        icon: "rotate",
        children: [
          { kind: "custom", id: "rot-presets", label: "Rotar contenido", hint: "Gira tinta, materia, imagenes y eje de simetria alrededor del centro de la vista. La vista (camara) se queda donde esta." },
          { kind: "number", id: "rot-canvas", label: "Rotar vista", min: -180, max: 180, step: 1, unit: "deg", hint: "Gira solo la camara (como rotar el papel). No toca el documento.", get: (s) => (s as unknown as { cameraRotation?: number }).cameraRotation ?? (editor.camera.rotation * 180) / Math.PI, set: (v) => editor.setCanvasRotation(v) },
          { kind: "action", id: "rot-reset-view", label: "Enderezar vista", run: () => editor.resetCanvasRotation() },
        ],
      },
    ],
  };

  // ----------------------------------------------------------- Explorar
  const view: Domain = {
    id: "view",
    label: "Explorar",
    icon: "compass",
    surfaces: ["radial"],
    children: [
      { kind: "action", id: "zoom-in", label: "Acercar", icon: "zoomIn", keepOpen: true, run: () => editor.zoomBy(1.25) },
      { kind: "action", id: "zoom-out", label: "Alejar", icon: "zoomOut", keepOpen: true, run: () => editor.zoomBy(1 / 1.25) },
      { kind: "action", id: "fit", label: "Encajar", icon: "fit", run: () => editor.fitView() },
      { kind: "action", id: "reset-view", label: "Reiniciar", icon: "grid", run: () => editor.resetView() },
      toolAction("hand", editor),
    ],
  };

  // ---------------------------------------------------------- Seleccion (Flecha universal · V)
  const select: Domain = {
    id: "select",
    label: "Seleccion",
    icon: "select",
    children: [
      toolAction("select", editor),
      {
        kind: "choice", id: "select-op", label: "Transformar", icon: "move", chooser: "segmented",
        hint: "Mover / Escalar / Rotar / Pivote: el pivote por defecto es el centro; Pivote lo desplaza. Respeta capas bloqueadas.",
        visible: (s) => s.tool === "select",
        options: [
          { value: "move", id: "select-op-move", label: "Mover" },
          { value: "scale", id: "select-op-scale", label: "Escalar" },
          { value: "rotate", id: "select-op-rotate", label: "Rotar" },
          { value: "pivot", id: "select-op-pivot", label: "Pivote" },
        ],
        get: (s) => s.selectOp, set: (v) => editor.setSelectOp(v as import("../../app/editor").SelectOp),
      },
      { kind: "toggle", id: "keep-aspect", label: "Mantener proporcion", icon: "lock", visible: (s) => s.tool === "select" && s.selectOp === "scale", get: (s) => s.keepAspect, set: (v) => editor.setKeepAspect(v) },
      { kind: "action", id: "select-all", label: "Seleccionar todo", icon: "select", run: () => editor.selectAll() },
      { kind: "action", id: "select-none", label: "Deseleccionar", icon: "close", disabled: (s) => s.selectCount === 0, run: () => editor.clearSelection() },
      { kind: "action", id: "select-duplicate", label: "Duplicar", icon: "duplicate", disabled: (s) => s.selectCount === 0, run: () => editor.duplicateSelection() },
      { kind: "action", id: "select-delete", label: "Borrar seleccion", icon: "trash", danger: true, disabled: (s) => s.selectCount === 0, run: () => editor.deleteSelection() },
      { kind: "custom", id: "select-align", label: "Alinear", surfaces: ["dock"] },
      { kind: "custom", id: "select-info", label: "Seleccion", surfaces: ["dock"] },
    ],
  };

  // ------------------------------------------------------------- Capas
  // Panel de capas estilo Photoshop: solo dock. Es un único campo `custom` cuyo
  // adaptador (que monta `LayersPanel`) se cablea en `buildCustoms` del dock;
  // aquí solo se declara su lugar, como con el selector de color.
  const layers: Domain = {
    id: "layers",
    label: "Capas",
    icon: "layers",
    surfaces: ["dock"],
    children: [
      { kind: "custom", id: "layers-panel", label: "Capas", surfaces: ["dock"] },
    ],
  };

  // Orden: Flecha primero (antes de Pincel), luego el resto. El dock lo refleja.
  return [file, select, brush, color, layers, matter, symmetry, view];
}
